import { isDevHubClient } from '../config/applications';
import {
  isCommunityFeatureEnabled,
  isConsultationsFeatureEnabled,
  isJourneyEnabled,
  isLoyaltyFeatureEnabled,
  isOnlineOrdersFeatureEnabled,
} from '../../data/domains/config';
import type { ScreenAudience, ScreenRegistryEntry, ScreenRegistryRouteName, ScreenStackRegistration } from './types';

/**
 * Single source of truth for app stack routes: audience, stack placement, deep links.
 * AuthStack / MainStack render from this list via `stackScreenFactory.tsx`.
 */
export const APP_SCREEN_REGISTRY: ScreenRegistryEntry[] = [
  // —— Guest welcome (auth stack only) ——
  { name: 'Main', audience: 'guest_only', stacks: 'auth', resolvedKey: 'Main', deepLink: '' },

  // —— Both stacks (shared route names) ——
  {
    name: 'Order',
    audience: 'both',
    stacks: 'auth',
    componentVariant: 'auth_order',
    deepLink: 'orders',
    skipAuthStack: () => !isOnlineOrdersFeatureEnabled(),
  },
  {
    name: 'DailyMenuFlow',
    audience: 'both',
    stacks: 'auth',
    componentVariant: 'auth_daily_menu',
    deepLink: 'menu',
  },
  { name: 'Consultation', audience: 'both', stacks: 'both', resolvedKey: 'Consultation', deepLink: 'consultation', skipStack: () => !isConsultationsFeatureEnabled() },
  {
    name: 'Journey',
    audience: 'both',
    stacks: 'both',
    resolvedKey: 'Journey',
    deepLink: 'journey',
    skipStack: () => !isJourneyEnabled(),
  },
  { name: 'JourneyResult', audience: 'both', stacks: 'both', resolvedKey: 'JourneyResult', skipStack: () => !isJourneyEnabled() },
  { name: 'SubscriptionList', audience: 'both', stacks: 'both', resolvedKey: 'SubscriptionList', deepLink: 'subscriptions' },
  { name: 'AboutUs', audience: 'both', stacks: 'both', resolvedKey: 'AboutUs', deepLink: 'about' },
  {
    name: 'HealthInformationSources',
    audience: 'both',
    stacks: 'both',
    resolvedKey: 'HealthInformationSources',
    deepLink: 'health-information',
  },
  {
    name: 'GuestMenuPreview',
    audience: 'both',
    stacks: 'both',
    resolvedKey: 'GuestMenuPreview',
    deepLink: 'guest-menu',
  },
  { name: 'TermsAndConditions', audience: 'both', stacks: 'both', resolvedKey: 'TermsAndConditions' },

  // —— Authenticated main stack only ——
  { name: 'MainTabs', audience: 'authenticated_only', stacks: 'main', componentVariant: 'main_tabs' },
  { name: 'Profile', audience: 'authenticated_only', stacks: 'main', resolvedKey: 'Profile', deepLink: 'profile', auth_only_deep_link: true },
  { name: 'MySubscriptions', audience: 'authenticated_only', stacks: 'main', resolvedKey: 'MySubscriptions' },
  { name: 'Checkout', audience: 'authenticated_only', stacks: 'main', resolvedKey: 'Checkout' },
  { name: 'OrderHistory', audience: 'authenticated_only', stacks: 'main', resolvedKey: 'OrderHistory', skipStack: () => !isOnlineOrdersFeatureEnabled() },
  { name: 'OrderDetails', audience: 'authenticated_only', stacks: 'main', resolvedKey: 'OrderDetails', skipStack: () => !isOnlineOrdersFeatureEnabled() },
  { name: 'Settings', audience: 'authenticated_only', stacks: 'main', resolvedKey: 'Settings', deepLink: 'settings', auth_only_deep_link: true },
  {
    name: 'DevTools',
    audience: 'both',
    stacks: 'both',
    resolvedKey: 'DevTools',
    skipStack: () => !isDevHubClient(),
  },
  { name: 'Notifications', audience: 'authenticated_only', stacks: 'main', resolvedKey: 'Notifications', deepLink: 'notifications', auth_only_deep_link: true },
  { name: 'Loyalty', audience: 'authenticated_only', stacks: 'main', resolvedKey: 'Loyalty', deepLink: 'loyalty', auth_only_deep_link: true, skipStack: () => !isLoyaltyFeatureEnabled() },
  { name: 'AddressLists', audience: 'authenticated_only', stacks: 'main', resolvedKey: 'AddressLists' },
  { name: 'DeliverySchedule', audience: 'authenticated_only', stacks: 'main', resolvedKey: 'DeliverySchedule' },
  { name: 'Community', audience: 'authenticated_only', stacks: 'main', resolvedKey: 'Community', deepLink: 'community', auth_only_deep_link: true, skipStack: () => !isCommunityFeatureEnabled() },
  { name: 'News', audience: 'authenticated_only', stacks: 'main', resolvedKey: 'News', deepLink: 'news', auth_only_deep_link: true },
  { name: 'NewsArticle', audience: 'authenticated_only', stacks: 'main', resolvedKey: 'NewsArticle' },
  { name: 'AddressForm', audience: 'authenticated_only', stacks: 'main', resolvedKey: 'AddressForm' },
  { name: 'FoodItemDetail', audience: 'authenticated_only', stacks: 'main', resolvedKey: 'FoodItemDetail' },
  { name: 'PaymentWebView', audience: 'authenticated_only', stacks: 'main', resolvedKey: 'PaymentWebView' },
  { name: 'OrderCheckout', audience: 'authenticated_only', stacks: 'main', resolvedKey: 'OrderCheckout', skipStack: () => !isOnlineOrdersFeatureEnabled() },
  { name: 'UpgradeCheckout', audience: 'authenticated_only', stacks: 'main', resolvedKey: 'UpgradeCheckout' },
  { name: 'OrderConfirmed', audience: 'authenticated_only', stacks: 'main', resolvedKey: 'OrderConfirmed', skipStack: () => !isOnlineOrdersFeatureEnabled() },
  { name: 'OrderFailed', audience: 'authenticated_only', stacks: 'main', resolvedKey: 'OrderFailed', skipStack: () => !isOnlineOrdersFeatureEnabled() },
  { name: 'AllergiesDislikes', audience: 'authenticated_only', stacks: 'main', resolvedKey: 'AllergiesDislikes' },
  { name: 'OrderCustomerDetails', audience: 'authenticated_only', stacks: 'main', resolvedKey: 'OrderCustomerDetails', skipStack: () => !isOnlineOrdersFeatureEnabled() },
];

export function getEffectiveAudience(entry: ScreenRegistryEntry): ScreenAudience {
  return entry.audience;
}

export function getRegistryEntry(route_name: string): ScreenRegistryEntry | undefined {
  return APP_SCREEN_REGISTRY.find(e => e.name === route_name);
}

export function getGuestOnlyRouteNames(): string[] {
  const names = new Set<string>();
  for (const entry of APP_SCREEN_REGISTRY) {
    if (getEffectiveAudience(entry) === 'guest_only') {
      names.add(entry.name);
    }
  }
  return [...names];
}

export function getScreensForStack(stack: 'auth' | 'main'): ScreenRegistryEntry[] {
  const seen = new Set<ScreenRegistryRouteName>();
  const out: ScreenRegistryEntry[] = [];

  for (const entry of APP_SCREEN_REGISTRY) {
    if (entry.stacks !== stack && entry.stacks !== 'both') {
      continue;
    }
    if (entry.skipStack?.()) {
      continue;
    }
    if (stack === 'auth' && entry.skipAuthStack?.()) {
      continue;
    }
    if (seen.has(entry.name)) {
      continue;
    }
    seen.add(entry.name);
    out.push(entry);
  }

  return out;
}

export function getAuthOnlyDeepLinkPrefixes(): string[] {
  const prefixes = new Set<string>();
  for (const entry of APP_SCREEN_REGISTRY) {
    if (entry.auth_only_deep_link && entry.deepLink) {
      prefixes.add(entry.deepLink);
    }
  }
  prefixes.add('dashboard');
  prefixes.add('calendar');
  prefixes.add('account');
  return [...prefixes];
}

export function buildDeepLinkScreenMaps(): {
  auth_screens: Record<string, string>;
  main_screens: Record<string, string>;
  main_tab_screens: Record<string, string>;
} {
  const auth_screens: Record<string, string> = {};
  const main_screens: Record<string, string> = {};
  const main_tab_screens: Record<string, string> = {};

  const tab_routes = new Set(['Dashboard', 'Calendar', 'DailyMenuFlow', 'Order', 'Account']);

  for (const entry of APP_SCREEN_REGISTRY) {
    if (!entry.deepLink) {
      continue;
    }
    if (entry.skipStack?.()) {
      continue;
    }
    if (entry.name === 'Main') {
      auth_screens[entry.deepLink] = entry.name;
      continue;
    }
    if (entry.name === 'MainTabs') {
      continue;
    }
    if (tab_routes.has(entry.name)) {
      main_tab_screens[entry.deepLink] = entry.name;
      continue;
    }
    if (entry.stacks === 'auth' || entry.stacks === 'both') {
      auth_screens[entry.deepLink] = entry.name;
    }
    if (entry.stacks === 'main' || entry.stacks === 'both') {
      main_screens[entry.deepLink] = entry.name;
    }
  }

  return { auth_screens, main_screens, main_tab_screens };
}
